import React, { useState } from 'react';
import '../pages-styles/contact.css';
import { usePageMeta } from '../hooks/usePageMeta';

function Contact() {
  usePageMeta(
    'Contact Magbity Agency Ltd - Football Agency',
    'Contact our professional football agency team for player representation, club services, and football consultancy. Email or book a consultation today.'
  );

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    enquiry_type: 'general',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const enquiryTypes = [
    { value: 'general', label: 'General Enquiry' },
    { value: 'player', label: 'Player Representation' },
    { value: 'club', label: 'Club Services' },
    { value: 'consultation', label: 'Consultation' },
    { value: 'other', label: 'Other' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Validate form
      if (!formData.name || !formData.email || !formData.message) {
        throw new Error('Please fill in all required fields');
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      
      const response = await fetch(`${apiUrl}/contact-enquiries/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          enquiry_type: formData.enquiry_type,
          message: formData.message
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || JSON.stringify(errorData) || 'Failed to submit enquiry');
      }

      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        enquiry_type: 'general',
        message: ''
      });

      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (err) {
      setError(err.message || 'An error occurred while submitting the enquiry');
      console.error('Submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section with Image */}
      <section className="contact-hero">
        <img src="/contact.jpg" alt="Contact us hero" className="hero-image" />
        <div className="hero-overlay">
          <div className="hero-content">
            <h1>Get In Touch</h1>
            <p>Have a question or enquiry? We'd love to hear from you. Contact us today or fill out the form below.</p>
          </div>
        </div>
      </section>



      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="contact-form-container">
          <h2>Send Us a Message</h2>
          <p className="form-intro">Fill out the form below and we'll get back to you as soon as possible.</p>

          <form className="contact-form" onSubmit={handleSubmit}>
            {submitted && (
              <div className="success-message">
                ✓ Thank you! Your enquiry has been submitted successfully. We'll review your message and contact you shortly.
              </div>
            )}
            {error && <div className="error-message">✗ Error: {error}</div>}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+44 123 456 7890"
                />
              </div>

              <div className="form-group">
                <label htmlFor="enquiry_type">Enquiry Type *</label>
                <select
                  id="enquiry_type"
                  name="enquiry_type"
                  value={formData.enquiry_type}
                  onChange={handleChange}
                  required
                >
                  {enquiryTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="What is your enquiry about?"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Please provide details about your enquiry..."
                rows="6"
                required
              ></textarea>
            </div>

            <button 
              type="submit" 
              className="submit-btn" 
              disabled={submitting}
            >
              {submitting ? 'Sending...' : 'Send Enquiry'}
            </button>
          </form>
        </div>
      </section>

      {/* Response Time Info */}
      <section className="response-section">
        <div className="response-container">
          <h3>Our Response Time</h3>
          <div className="response-grid">
            <div className="response-item">
              <div className="response-icon">⚡</div>
              <p><strong>24 Hours</strong><br/>Standard enquiries</p>
            </div>
            <div className="response-item">
              <div className="response-icon">🎯</div>
              <p><strong>2-4 Hours</strong><br/>Urgent matters</p>
            </div>
            <div className="response-item">
              <div className="response-icon">📞</div>
              <p><strong>Direct Phone</strong><br/>Available during business hours</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
